import ChatMessage, { MessageRole } from "../models/chat_message";
import { agentEndpoints } from "./apiConfig";
import { Result } from "../models/result";
import * as chatUtils from "../utils/chat_utils";
import { MessageExportSourceType } from "../constants/constants";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
} from "firebase/firestore";

import httpRepo from "./httpRepo";


export const getChatResponse = async (params) => {
  try {
    const { schoolInfo, prompt, previousMessages, userInfo, userToken } = params;

    const response = await fetch(agentEndpoints.getResponse, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${userToken}` },
      body: JSON.stringify({ schoolInfo, prompt, previousMessages, userInfo }),
    });

    if (!response.ok) {
      const res = await response.json();
      let message = `Failed to get chat response`;
      if (res && res.message) {
        message = `${message}: ${res.message}`;
      } else {
        message = `${message}: Unknown error`;
      }
      throw new Error(message);
    }

    const data = await response.json();
    console.log(data);
    return data.value;
  } catch (error) {
    console.error("Error getting chat response:", error);
    throw error;
  }
};

export const createNewSession = async (data) => {
  try {
    const response = await httpRepo.post(agentEndpoints.createNewSession, data);
    return response;
  } catch (ex) {
    return new Result({
      message: `Error creating new session: ${ex.message}`,
    });
  }
};

export const getPreviousMessages = (
  /** @type {[ChatMessage]} */ messageList
) => {
  let previousMessages = [];
  if (messageList.length) {
    previousMessages = messageList.map((message) => {
      return {
        role: message.role == MessageRole.ASSISTANT ? "model" : message.role,
        content: [
          {
            text: message.content ?? "",
          },
        ],
      };
    });
  }
  return previousMessages;
};

export const clearChatSession = async (data) => {
  const { userId, schoolId, sessionId } = data;
  try {
    const response = await httpRepo.post(agentEndpoints.clearChatSession, {
      userId, schoolId, sessionId
    });
    if (!response.success) {
      throw new Error("Failed to clear chat session");
    }
    return response;
  } catch (ex) {
    return new Result({
      message: `Error clearing chat session: ${ex.message}`,
    });
  }
};

export const getChatHistory = async (data) => {
  try {
    const { userId, schoolId, sessionId } = data;
    const result = await httpRepo.post(agentEndpoints.getChatHistory, { userId, schoolId, sessionId });



    if (result.success) {
      // Convert the messages to ChatMessage objects
      const messages = result.value.messages.map((messageData) =>
        ChatMessage.fromJson(messageData)
      );
      return new Result({
        value: messages,
      });
    } else {
      return result;
    }
  } catch (ex) {
    return new Result({
      message: `Error getting chat history: ${ex.message}`,
    });
  }
};

export const getUserChats = async (data) => {
  try {
    const { userId, schoolId } = data;
    const response = await httpRepo.post(agentEndpoints.getUserChats, { userId, schoolId });
    return response;
  } catch (ex) {
    return new Result({
      message: `Error getting user chats: ${ex.message}`,
    });
  }
};

export const getMessageById = async (data) => {
  try {
    const { schoolId, messageId, type, reportId, secretKey } = data;
    
    let url;
    if(type == MessageExportSourceType.BULK_REPORT){
      url = `${agentEndpoints.getBulkReportItemById}?schoolId=${schoolId}&reportId=${reportId}&itemId=${messageId}`;

    }else if(type == MessageExportSourceType.STUDENT_REPORT){
      url = `${agentEndpoints.getMessageById}?schoolId=${schoolId}&messageId=${messageId}&type=${type}`;

    }else if(type == MessageExportSourceType.REPORT){
      url = `${agentEndpoints.getMessageById}?schoolId=${schoolId}&messageId=${messageId}&type=${type}&reportId=${reportId}`;
    }else{
      url = `${agentEndpoints.getMessageById}?schoolId=${schoolId}&messageId=${messageId}`
    }

    
    let isSecretSecured = secretKey??false;
    let options = {
      isSecretSecured: isSecretSecured
    }
    if(isSecretSecured){
      options.headers = {"api-secret": secretKey}
    }

    if(type == MessageExportSourceType.STUDENT_REPORT){
      options.isPublicAPI = true;
    }
    const response = await httpRepo.get(url, options);
    return response;
  } catch (ex) {
    return new Result({
      message: `Error [getMessageById]: ${ex.message}`,
    });
  }
};

export const exportChatMessage = async (data) => {
  try {
    const {
      id,
      schoolId,
      messageId,
      reportName = 'ask-me',
      reportId,
      type,
      schoolData,
      iconUrl = schoolData?.iconUrl,
    } = data;

    const requestData = {
      schoolId,
      messageId,
      reportName,
      reportId,
      type,
      schoolData,
      iconUrl,
    };


    const result = await httpRepo.postBlob(agentEndpoints.exportChatMessage, requestData);
    
    if (result.success) {
      return result; // Returns Result with blob in value
    } else {
      return result; // Returns Result with error message
    }
  } catch (ex) {
    console.error("Error [exportChatMessage]:", ex);
    return new Result({
      message: `Error [exportChatMessage]: ${ex.message}`,
    });
  }
};

export const streamAgentResponse = async (params, onMessageCallback) => {
  try {
    const {
      schoolInfo,
      prompt,
      previousMessages,
      userInfo,
      noCache,
      isAgentSwitch,
      sessionId,
      roleToken,
    } = params;
    ///the server looks for [schoolId] param inside [schoolInfo]. But the schoolData
    /// has [id] property. So we need to rename it to [schoolId]
    schoolInfo.schoolId = schoolInfo.id;
    const response = await httpRepo.postStream(agentEndpoints.getResponse, {
        schoolInfo,
        prompt,
        previousMessages,
        userInfo,
        noCache,
        isAgentSwitch: isAgentSwitch ?? false,
        sessionId,
        persistentSession: true
    }, {
      headers: {
       "role-token": roleToken
     },
    });

   if(!response.success){
    throw response;
   }

    const reader = response.value.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    const processLine = (line, isDone = false) => {
      if (line) {
        if (line.startsWith("data:")) {
          const jsonPayload = line.substring(5).trim();
          if (jsonPayload) {
            const data = JSON.parse(jsonPayload);
            console.log(`data: ${JSON.stringify(data)}`);
            const messageId = data.messageId;
            ///if the data is metaData, then we need to process it differently
            if (data.metadata) {
              onMessageCallback({
                type: "metadata",
                data: data.metadata,
                messageId: messageId,
                isDone: isDone,
              });
              return;
            }
            let content = data.content;
            if (content) {
              // The 'content' field can be a JSON string or a plain string
              try {
                // Try to parse it as JSON
                JSON.parse(content);
                onMessageCallback({
                  type: "json",
                  data: content,
                  messageId: messageId,
                  isDone: isDone,
                  isError: content.error ?? false,
                });
              } catch (e) {
                content = chatUtils.replaceEscapedNewlines(content);
                // If it fails, it's a plain string
                onMessageCallback({
                  type: "string",
                  data: content,
                  messageId: messageId,
                  isDone: isDone,
                });
              }
            }else {
              if (data.error) {
                onMessageCallback({
                  type: "string",
                  data: data.error,
                  messageId: messageId,
                  isDone: isDone,
                  isError: true,
                });
              }
          }
          }
        }
      } else if (isDone) {
        onMessageCallback({ type: "string", data: "", isDone: isDone });
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        // Process any remaining data in the buffer
        //if (buffer.length > 0) {
        processLine(buffer, true);

        // }
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      let newlineIndex;
      while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
        const line = buffer.substring(0, newlineIndex);
        buffer = buffer.substring(newlineIndex + 1);
        processLine(line);
      }
    }
  } catch (error) {
    console.error("Error streaming agent response:", error);
    // The user provided a change to return a Result object on error.
    // Assuming Result class is available in scope.
    // This part of the code is kept as per the user's recent change.
    onMessageCallback({
      type: "string",
      data: error.message,
      isDone: true,
      isError: true,
    });
    return new Result({
      message: `Error streaming agent response: ${error.message}`,
      code: "STREAM_ERROR",
    });
  }
};

export const subscribeToChatList = (schoolId, userId, onDataCallback) => {
  try {
    const db = getFirestore();
    const q = query(
      collection(db, "schools", schoolId, "chat_sessions"),
      where("userId", "==", userId),
      orderBy("lastTimestamp", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const chatSessions = [];
        querySnapshot.forEach((doc) => {
          chatSessions.push(doc.data());
        });

        // No manual sorting needed - Firestore orderBy already handles this

        onDataCallback(chatSessions);
      },
      (error) => {
        console.error("Error in chat list subscription:", error);
        onDataCallback(null, error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error("Error setting up chat list subscription:", error);
    throw error;
  }
};
